import { getColorMode } from '@ordo/auth'
import { applyDefaultThemePreference } from 'hooks/useTheme'
import type { User } from 'types/users'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

interface AuthState {
  user: User | null
  loading: boolean
  lastFetchedAt: number
  fetchUser: () => Promise<void>
  setUser: (user: User) => void
  clearUser: () => void
}

// Single in-flight fetch shared across every concurrent fetchUser caller.
// Multiple components can mount in the same tick (route guards, sidebar,
// page bodies) — without this dedup, each one fires its own /api/users/me
// which fans out into duplicate multipass /me calls server-side.
let inFlight: Promise<void> | null = null

// The `user` field is persisted to localStorage so returning visitors get
// an OPTIMISTIC first render: ProtectedRoute/AuthedShell mount the app (and
// its data queries fire) immediately from the last-known user while
// `fetchUser` revalidates the session in the background. A 401 on that
// revalidation clears the user (persist writes the null through) and
// redirects to /login exactly as before — the trade is display-only
// staleness (sidebar name/avatar, admin nav gate) for at most one request;
// every API call is still authorized server-side.
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      loading: false,
      lastFetchedAt: -1,

      fetchUser: async () => {
        if (inFlight) {
          return inFlight
        }
        const { lastFetchedAt } = get()
        if (Date.now() - lastFetchedAt < CACHE_TTL_MS) {
          return
        }

        set({ loading: true })
        inFlight = (async () => {
          try {
            const res = await fetch('/api/users/me', {
              headers: { 'Content-Type': 'application/json' },
            })
            if (res.status === 401) {
              set({ user: null, loading: false, lastFetchedAt: 0 })
              return
            }
            if (!res.ok) {
              // Server error. With a persisted user the app keeps rendering
              // (data requests surface their own failures); with none, mark
              // the fetch resolved so ProtectedRoute falls through to /login
              // instead of hanging on the skeleton forever.
              set(get().user ? { loading: false } : { loading: false, lastFetchedAt: 0 })
              return
            }
            const user = (await res.json()) as User
            applyDefaultThemePreference(getColorMode(user))
            set({ user, loading: false, lastFetchedAt: Date.now() })
          } catch {
            set(get().user ? { loading: false } : { loading: false, lastFetchedAt: 0 })
          } finally {
            inFlight = null
          }
        })()
        return inFlight
      },

      setUser: (user: User) => {
        set({ user, lastFetchedAt: Date.now() })
      },

      clearUser: () => {
        set({ user: null, lastFetchedAt: 0 })
      },
    }),
    {
      name: 'boombox-auth',
      // Persist ONLY the user snapshot — loading/lastFetchedAt must reset
      // every page load so a fresh revalidation always fires.
      partialize: (state) => ({ user: state.user }),
    },
  ),
)
